import React, { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { assetsSavedSearchRetrieve } from '@goldcast/api/content';
import SessionDetailLayout from '../SessionDetail/SessionDetailLayout';
import { NavItemType } from '../SessionDetail/components/NavItem';
import { NavigationEndpointConfig } from '../SessionDetail/constants';
import { NavigationEndpoint } from '../SessionDetail/types';
import { clearSavedAISearch, setSavedAISearch } from '@/stores/savedAISearch';
import { useAppContext } from '@/context/AppContext/AppContext';
import { showErrorToast } from '@/libs/toast/toast';
import { unseenClipIds } from '@/stores/unseenClips';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { getErrorDiagnostics } from '@/libs/errorDiagnostics';

export default function SavedSearchDetailLayout({ isLoading }: { isLoading: boolean }) {
  const unseenClipsStore = useSyncExternalStore(unseenClipIds.subscribe, unseenClipIds.getSnapshot);
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const useTextGrouping = Boolean(featureFlags[FeatureFlagKeys.Use_CL_Text_Assets_Grouping]);
  const { id } = useParams<{ id: string }>();
  const { logger } = useAppContext();
  const navigate = useNavigate();

  useEffect(() => {
    assetsSavedSearchRetrieve({ id: id! })
      .then(res => setSavedAISearch(res))
      .catch(err => {
        logger.error('[SavedSearchDetailLayout] Failed to load saved search', {
          savedSearchId: id,
          ...getErrorDiagnostics(err)
        });
        showErrorToast(`Whoops! We hit a snag while fetching the saved search. Please try again.`);
        navigate(-1);
      });

    return () => {
      clearSavedAISearch();
    };
  }, [id]);

  const onClickClipHandler = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    EventBus.dispatch(CustomEvents.ClipListPopUp, { isOpen: true });
  }, []);

  const nav: NavItemType[] = useMemo(() => {
    const textConfig = NavigationEndpointConfig[NavigationEndpoint.text];
    return [
      {
        iconName: 'IconPlayerPlay',
        filledIconName: 'IconPlayerPlayFilled',
        label: 'Clips',
        href: `/search/${id}/clips`,
        unseenCount: unseenClipsStore.length,
        visible: true,
        onClick: onClickClipHandler
      },
      ...(useTextGrouping
        ? [
            {
              iconName: textConfig.iconName,
              filledIconName: textConfig.filledIconName,
              label: textConfig.label,
              href: `/search/${id}/social`,
              visible: true
            }
          ]
        : [
            {
              iconName: 'IconThumbUp' as const,
              filledIconName: 'IconThumbUpFilled' as const,
              label: 'Social',
              href: `/search/${id}/social`,
              visible: true
            },
            {
              iconName: 'IconArticle' as const,
              filledIconName: 'IconArticleFilled' as const,
              label: 'Blog',
              href: `/search/${id}/blog`,
              visible: true
            }
          ])
    ];
  }, [id, unseenClipsStore.length, useTextGrouping]);

  return <SessionDetailLayout isLoading={isLoading} navMenu={nav} />;
}
